import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  MicrophoneIcon,
  StopIcon,
  PlayIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { RootState, AppDispatch } from '../../store/store';
import {
  startVoiceSession,
  endVoiceSession as stopVoiceSession,
  setListening,
  setTranscript,
  setConnectionStatus,
  setMenuItems,
} from '../../store/slices/voiceSlice';
import { UltravoxSession } from 'ultravox-client';
import { useWebSocket } from '../../hooks/useWebSocket';
import { logVoice, logWebSocket } from '../../utils/fileLogger';
import { generateReceiptPDF } from '../../utils/receiptPdf';

interface UltravoxVoiceInterfaceProps {
  tableNumber?: string;
}

const UltravoxVoiceInterface: React.FC<UltravoxVoiceInterfaceProps> = ({
  tableNumber,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    isListening,
    isProcessing,
    currentSession,
    transcript,
    error,
    connectionStatus,
    menuItems,
    currentCart,
    currentOrder,
    currentBooking,
    customerInfo,
  } = useSelector((state: RootState) => state.voice);

  // Debug: Log cart changes
  useEffect(() => {
    console.log('🔍 Cart state changed:', {
      hasCart: !!currentCart,
      itemCount: currentCart?.itemCount,
      total: currentCart?.total,
      items: currentCart?.items
    });
  }, [currentCart]);

  // Debug: Log order changes
  useEffect(() => {
    console.log('📦 ORDER STATE CHANGED:', {
      hasOrder: !!currentOrder,
      orderNumber: currentOrder?.orderNumber,
      total: currentOrder?.total,
      itemsCount: currentOrder?.items?.length,
      customer: currentOrder?.customer?.name
    });
  }, [currentOrder]);

  // Debug: Log customer info changes
  useEffect(() => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('👤 COMPONENT: CUSTOMER INFO CHANGED IN STATE');
    console.log('   customerInfo object:', customerInfo);
    console.log('   Has name?', !!customerInfo?.name);
    console.log('   Has phone?', !!customerInfo?.phone);
    console.log('   Has email?', !!customerInfo?.email);
    console.log('   Has address?', !!customerInfo?.address);
    console.log('   Should display card?', !!(ultravoxSession && customerInfo));
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }, [customerInfo]);

  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [isLoadingMenu, setIsLoadingMenu] = useState(true);
  const { joinSession, leaveSession, onEndCall } = useWebSocket();

  // Fetch menu items on component mount
  useEffect(() => {
    const fetchMenuItems = async () => {
      setIsLoadingMenu(true);
      try {
        console.log('🍽️ Fetching menu items on mount...');
        const company = process.env.REACT_APP_COMPANY || 'QASTCO';
        const response = await fetch(`${process.env.REACT_APP_ERPNEXT_BASE_URL}/api/method/webvoiceagent.webvoiceagent.api.voice_api.get_menu_items?company=${company}`);

        if (!response.ok) {
          console.error('❌ Failed to fetch menu items:', response.statusText);
          setIsLoadingMenu(false);
          return;
        }

        const result = await response.json();
        const items = result.message?.data || result.data || [];

        if (items.length > 0) {
          console.log('✅ Loaded', items.length, 'menu items');
          dispatch(setMenuItems(items));
        } else {
          console.log('⚠️ No menu items returned from API');
        }
      } catch (error) {
        console.error('❌ Error fetching menu items:', error);
      } finally {
        setIsLoadingMenu(false);
      }
    };

    fetchMenuItems();
  }, [dispatch]);

  const [ultravoxSession, setUltravoxSession] = useState<UltravoxSession | null>(null);

  // Register callback to end Ultravox call when backend sends end_call event
  useEffect(() => {
    onEndCall(async () => {
      console.log('🔚 End call callback triggered - ending Ultravox session');

      // Leave Ultravox call
      if (ultravoxSession) {
        ultravoxSession.leaveCall();
        setUltravoxSession(null);
        console.log('✅ Ultravox session ended');
      }

      // Clear session from Redux to hide the ribbon (but keep currentOrder for receipt!)
      if (currentSession?.sessionId) {
        console.log('🧹 Clearing voice session from state');
        await dispatch(stopVoiceSession(currentSession.sessionId));
      }

      // Clear UI state but DON'T call leaveSession() which would clear the order
      setConversationLog([]);
      setIsStarting(false);
      dispatch(setTranscript(''));

      console.log('✅ Session cleared but order data preserved for receipt');
    });
  }, [ultravoxSession, onEndCall, currentSession, dispatch]);
  const [conversationLog, setConversationLog] = useState<Array<{role: string, text: string}>>([]);
  const [isStarting, setIsStarting] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const ultravoxSessionRef = useRef<UltravoxSession | null>(null);
  const leaveSessionRef = useRef(leaveSession);

  useEffect(() => {
    ultravoxSessionRef.current = ultravoxSession;
  }, [ultravoxSession]);

  useEffect(() => {
    leaveSessionRef.current = leaveSession;
  }, [leaveSession]);

  useEffect(() => {
    return () => {
      if (ultravoxSessionRef.current) {
        ultravoxSessionRef.current.leaveCall();
      }
      leaveSessionRef.current();
    };
  }, []);

  const startSession = async () => {
    if (isStarting || ultravoxSession) {
      console.log('⚠️ Session already starting or active, ignoring click');
      return;
    }

    setIsStarting(true);
    console.log('🚀 Starting new session...');

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const result = await dispatch(startVoiceSession({ tableNumber: tableNumber || '1' })).unwrap();

      logVoice.sessionStarted(result.data?.sessionId || result.sessionId || 'NO_SESSION_ID');
      console.log('🔍 Full result object:', JSON.stringify(result, null, 2));

      if (result.success && result.data?.joinUrl) {
        const sessionId = result.data?.sessionId || result.sessionId;
        if (sessionId) {
          logWebSocket.joiningSession(sessionId);
          joinSession(sessionId);
        } else {
          console.error('❌ NO SESSION ID FOUND in result:', result);
        }

        const session = new UltravoxSession();
        setUltravoxSession(session);

        session.addEventListener('status', (event: any) => {
          console.log('Ultravox status:', event.state);

          if (event.state === 'idle') {
            dispatch(setConnectionStatus('disconnected'));
            dispatch(setListening(false));
          } else if (event.state === 'listening') {
            dispatch(setConnectionStatus('connected'));
            dispatch(setListening(true));
          } else if (event.state === 'thinking') {
            dispatch(setConnectionStatus('connected'));
            dispatch(setListening(false));
          } else if (event.state === 'speaking') {
            dispatch(setConnectionStatus('connected'));
            dispatch(setListening(false));
          }
        });

        session.addEventListener('transcripts', (event: any) => {
          console.log('📝 Transcripts event received:', event);

          if (event.role === 'user' && event.text) {
            console.log('👤 User said:', event.text);
            dispatch(setTranscript(event.text));
            setConversationLog(prev => [...prev, { role: 'user', text: event.text }]);
          }

          if (event.role === 'agent' && event.text) {
            console.log('🤖 Agent said:', event.text);
            setConversationLog(prev => [...prev, { role: 'agent', text: event.text }]);
          }
        });

        session.addEventListener('experimental_message', (msg: any) => {
          console.log('🔬 Experimental message:', msg);

          if (msg.type === 'transcript' || msg.role) {
            console.log('📝 Transcript from experimental_message:', msg);
            if (msg.role === 'user' && msg.text) {
              console.log('👤 User (experimental):', msg.text);
              setConversationLog(prev => [...prev, { role: 'user', text: msg.text }]);
            }
            if (msg.role === 'agent' && msg.text) {
              console.log('🤖 Agent (experimental):', msg.text);
              setConversationLog(prev => [...prev, { role: 'agent', text: msg.text }]);
            }
          }
        });

        session.joinCall(result.data.joinUrl);

        console.log('✅ Ultravox session started successfully');
        setIsStarting(false);

      } else {
        throw new Error('Failed to get joinUrl from backend');
      }
    } catch (error: any) {
      console.error('❌ Error starting Ultravox session:', error);
      dispatch(setConnectionStatus('error'));
      setIsStarting(false);
    }
  };

  const stopSession = async () => {
    if (ultravoxSession) {
      ultravoxSession.leaveCall();
      setUltravoxSession(null);
    }

    if (currentSession?.sessionId) {
      await dispatch(stopVoiceSession(currentSession.sessionId));
    }

    setConversationLog([]);
    setIsStarting(false);
    dispatch(setTranscript(''));
  };

  // Group menu items by category
  const menuByCategory = menuItems.reduce((acc, item) => {
    const category = item.item_group || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {} as Record<string, typeof menuItems>);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <audio ref={audioRef} autoPlay />

      {/* Hero Section */}
      {/* <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-3">
              🎤 Voice Ordering System
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-2">
              Order your favorite food with your voice
            </p>
            <p className="text-sm md:text-base text-blue-200">
              {tableNumber && `Table ${tableNumber} • `}
              Fast, Easy, and Hands-Free
            </p>
          </div>
        </div>
      </div> */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Main Control Panel */}
        <div
          className="rounded-2xl shadow-2xl p-6 mb-6"
          style={{
            background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)'
          }}
        >
          {!ultravoxSession ? (
            <div className="text-center">
              <div className="text-white mb-6">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">Ready to Order?</h2>
                <p className="text-lg opacity-90">
                  {isStarting ? 'Connecting to AI Assistant...' : 'Click below to start voice ordering'}
                </p>
              </div>
              <button
                onClick={startSession}
                disabled={isProcessing || isStarting}
                className="bg-white rounded-full p-8 shadow-2xl transition-all transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed mx-auto inline-flex items-center justify-center"
                style={{ color: 'var(--color-primary)' }}
              >
                {isStarting ? (
                  <div
                    className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: 'var(--color-primary)', borderTopColor: 'transparent' }}
                  ></div>
                ) : (
                  <PlayIcon className="w-16 h-16" />
                )}
              </button>
              {!isStarting && (
                <p className="text-white text-sm mt-4 opacity-75">
                  🎯 Tip: Make sure your microphone is enabled
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div
                  className={`rounded-full p-4 shadow-xl ${isListening ? 'animate-pulse' : ''}`}
                  style={{
                    backgroundColor: isListening ? '#ef4444' : 'white',
                    color: isListening ? 'white' : 'var(--color-primary)'
                  }}
                >
                  <MicrophoneIcon className="w-10 h-10" />
                </div>
                <div className="text-white">
                  <p className="font-bold text-2xl">
                    {isListening ? '🎙️ Listening...' : '🤔 Processing...'}
                  </p>
                  <p className="text-lg opacity-90">
                    Speak naturally to place your order
                  </p>
                </div>
              </div>
              <button
                onClick={stopSession}
                className="bg-red-500 hover:bg-red-600 text-white rounded-xl px-8 py-4 shadow-xl transition-all transform hover:scale-105 flex items-center gap-3"
              >
                <StopIcon className="w-6 h-6" />
                <span className="font-semibold text-lg">End Session</span>
              </button>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">❌</span>
              <p className="text-red-800 font-medium text-lg">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Session Active Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Live Transcript */}
            {transcript && ultravoxSession && (
              <div
                className="rounded-xl p-5 shadow-lg border-l-4"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderLeftColor: 'var(--color-primary)'
                }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl"
                    style={{ backgroundColor: 'var(--color-primary)' }}
                  >
                    👤
                  </div>
                  <div className="flex-1">
                    <h3
                      className="font-semibold mb-2 text-lg"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      You said:
                    </h3>
                    <p className="text-base" style={{ color: 'var(--color-text-secondary)' }}>"{transcript}"</p>
                  </div>
                </div>
              </div>
            )}

            {/* Conversation History */}
            {conversationLog.length > 0 && ultravoxSession && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-4 text-2xl flex items-center gap-2">
                  💬 Conversation
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({conversationLog.length} messages)
                  </span>
                </h3>
                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                  {conversationLog.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`flex items-start gap-3 max-w-[85%] ${
                        message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                      }`}>
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-xl shadow-md ${
                          message.role === 'user'
                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                            : 'bg-gradient-to-r from-green-500 to-green-600 text-white'
                        }`}>
                          {message.role === 'user' ? '👤' : '🤖'}
                        </div>
                        <div className={`rounded-2xl px-5 py-3 shadow-md ${
                          message.role === 'user'
                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                            : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-900'
                        }`}>
                          <p className="text-base leading-relaxed">{message.text}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Menu Display - Show when no session or session active, but hide when receipt is shown */}
            {(!ultravoxSession || menuItems.length > 0) && !currentOrder && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900 text-2xl flex items-center gap-2">
                    📋 Our Menu
                    {menuItems.length > 0 && (
                      <span className="text-sm font-normal text-gray-500">
                        ({menuItems.length} items)
                      </span>
                    )}
                  </h3>
                  {!isLoadingMenu && menuItems.length > 0 && (
                    <span className="text-xs flex items-center text-green-600">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></span>
                      Live Menu
                    </span>
                  )}
                </div>

                {isLoadingMenu ? (
                  <div className="text-center py-12">
                    <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-gray-600">Loading menu...</p>
                  </div>
                ) : menuItems.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🍽️</div>
                    <p className="text-gray-600 text-lg">No menu items available</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(menuByCategory).map(([category, items]) => (
                      <div key={category}>
                        <h4 className="font-bold text-lg text-gray-800 mb-3 pb-2 border-b-2 border-gray-200">
                          {category}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {items.map((item, index) => (
                            <div
                              key={index}
                              className="rounded-xl overflow-hidden border-2 hover:border-blue-400 shadow-sm hover:shadow-md transition-all bg-gradient-to-br from-white to-gray-50"
                              style={{ borderColor: 'var(--color-surface)' }}
                            >
                              {item.image && (
                                <div className="w-full h-40 overflow-hidden bg-gray-100">
                                  <img
                                    src={item.image}
                                    alt={item.item_name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      // Hide image if it fails to load
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                              <div className="p-4">
                                <div className="flex justify-between items-start mb-2">
                                  <h5 className="font-semibold text-base text-gray-900 flex-1">
                                    {item.item_name}
                                  </h5>
                                  <span className="text-lg font-bold text-blue-600 ml-2">
                                    £{item.standard_rate.toFixed(2)}
                                  </span>
                                </div>
                                {item.description && (
                                  <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                                    {item.description}
                                  </p>
                                )}
                                <p className="text-xs text-gray-400">
                                  Code: {item.item_code}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Cart (Sticky) */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-6 space-y-6">
              {/* Customer Information Card */}
              {ultravoxSession && customerInfo && (
                <div
                  className="rounded-2xl shadow-2xl border-2 overflow-hidden"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-primary)'
                  }}
                >
                  <div
                    className="p-5"
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    }}
                  >
                    <h3 className="font-bold text-xl flex items-center gap-2 text-white">
                      👤 Your Information
                    </h3>
                  </div>

                  <div className="p-5 space-y-3">
                    {customerInfo.name && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                        <span className="text-2xl">📝</span>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Name</p>
                          <p className="font-semibold text-gray-900">{customerInfo.name}</p>
                        </div>
                      </div>
                    )}

                    {customerInfo.phone && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                        <span className="text-2xl">📞</span>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Phone</p>
                          <p className="font-semibold text-gray-900">{customerInfo.phone}</p>
                        </div>
                      </div>
                    )}

                    {customerInfo.email && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                        <span className="text-2xl">✉️</span>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Email</p>
                          <p className="font-semibold text-gray-900">{customerInfo.email}</p>
                        </div>
                      </div>
                    )}

                    {customerInfo.address && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                        <span className="text-2xl">🏠</span>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Address</p>
                          <p className="font-semibold text-gray-900">
                            {typeof customerInfo.address === 'string'
                              ? customerInfo.address
                              : customerInfo.address.street || JSON.stringify(customerInfo.address)}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
                      <p className="text-xs text-gray-600">
                        💡 Need to change? Just tell me the correct information
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {ultravoxSession ? (
                /* Active Cart During Session */
                <div
                  className="rounded-2xl shadow-2xl border-2 overflow-hidden"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-primary)'
                  }}
                >
                  <div
                    className="p-5"
                    style={{
                      background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)'
                    }}
                  >
                    <h3 className="font-bold text-2xl flex items-center gap-2 text-white">
                      <ShoppingCartIcon className="w-7 h-7" />
                      Your Order
                    </h3>
                    <p className="text-sm text-white opacity-90 mt-1">
                      {!currentCart || currentCart.itemCount === 0
                        ? 'Start adding items...'
                        : `${currentCart.itemCount} ${currentCart.itemCount === 1 ? 'item' : 'items'}`}
                    </p>
                  </div>

                  <div className="p-5">
                    {!currentCart || currentCart.itemCount === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-6xl mb-3">🛒</div>
                        <p className="font-semibold mb-2 text-gray-900">
                          Cart is Empty
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                          Say something like:
                        </p>
                        <div className="bg-blue-50 rounded-lg p-4 text-left space-y-2">
                          <p className="text-sm text-gray-700">💬 "I'd like a burger"</p>
                          <p className="text-sm text-gray-700">💬 "Add a coffee"</p>
                          <p className="text-sm text-gray-700">💬 "Give me a pizza"</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {currentCart.items.map((item, idx) => (
                          <div
                            key={idx}
                            className="bg-white rounded-xl p-4 shadow-md border-2 hover:shadow-lg transition-shadow"
                            style={{ borderColor: 'var(--color-surface)' }}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3 flex-1">
                                <span className="font-bold text-xl text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
                                  {item.quantity}×
                                </span>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900">
                                    {item.itemName}
                                  </h4>
                                  <p className="text-sm text-gray-500 mt-1">
                                    £{item.rate.toFixed(2)} each
                                  </p>
                                </div>
                              </div>
                              <div className="text-right ml-2">
                                <p className="font-bold text-xl text-blue-600">
                                  £{item.total.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}

                        <div className="mt-6 pt-6 border-t-2" style={{ borderColor: 'var(--color-surface)' }}>
                          <div className="flex items-center justify-between mb-4">
                            <span className="font-bold text-xl text-gray-900">Total:</span>
                            <span className="font-bold text-3xl text-blue-600">
                              £{currentCart.total.toFixed(2)}
                            </span>
                          </div>

                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200">
                            <p className="text-sm text-center font-medium text-gray-700">
                              💡 Say <strong>"confirm my order"</strong> when ready
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Getting Started Guide When No Session */
                <div
                  className="rounded-2xl shadow-xl p-6 border-2"
                  style={{
                    background: 'linear-gradient(135deg, var(--color-surface) 0%, var(--color-background) 100%)',
                    borderColor: 'var(--color-primary)'
                  }}
                >
                  <h3 className="font-bold text-2xl mb-4" style={{ color: 'var(--color-text-primary)' }}>
                    🎯 How to Order
                  </h3>
                  <ol className="space-y-4">
                    <li className="flex items-start gap-3">
                      <span
                        className="flex-shrink-0 w-8 h-8 text-white rounded-full flex items-center justify-center font-bold"
                        style={{ backgroundColor: 'var(--color-primary)' }}
                      >
                        1
                      </span>
                      <div>
                        <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Start Voice Session</p>
                        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Click the play button above</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span
                        className="flex-shrink-0 w-8 h-8 text-white rounded-full flex items-center justify-center font-bold"
                        style={{ backgroundColor: 'var(--color-primary)' }}
                      >
                        2
                      </span>
                      <div>
                        <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Tell Us What You Want</p>
                        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Say item names from the menu</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span
                        className="flex-shrink-0 w-8 h-8 text-white rounded-full flex items-center justify-center font-bold"
                        style={{ backgroundColor: 'var(--color-primary)' }}
                      >
                        3
                      </span>
                      <div>
                        <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Confirm Your Order</p>
                        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Say "confirm my order" to complete</p>
                      </div>
                    </li>
                  </ol>

                  <div className="mt-6 pt-6 border-t-2" style={{ borderColor: 'var(--color-border)' }}>
                    <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>💬 Example Commands:</p>
                    <div className="space-y-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      <p>• "I want a burger and fries"</p>
                      <p>• "Add two coffees"</p>
                      <p>• "Remove the pizza"</p>
                      <p>• "What desserts do you have?"</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Confirmation Display */}
        {currentOrder && (
          <div className="mt-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-xl p-6 border-2 border-green-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-2xl">✅</span>
              </div>
              <div>
                <h3 className="font-bold text-2xl text-gray-900">Order Confirmed!</h3>
                <p className="text-sm text-gray-600">Your order has been placed successfully</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Total Amount:</p>
                <p className="text-2xl font-bold text-green-600">£{currentOrder.total.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Payment:</p>
                <p className="text-gray-900 capitalize">{currentOrder.paymentMethod}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Customer:</p>
                <p className="text-gray-900">{currentOrder.customer.name}</p>
                <p className="text-sm text-gray-600">{currentOrder.customer.phone}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Order Type:</p>
                <p className="text-gray-900 capitalize">{currentOrder.orderType}</p>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t-2 border-green-200">
              <p className="text-sm font-semibold text-gray-700 mb-3">Items:</p>
              <div className="space-y-2">
                {currentOrder.items.map((item: any, idx: number) => (
                  <div key={idx} className="flex justify-between text-gray-700">
                    <span>{item.quantity}× {item.itemName}</span>
                    <span className="font-semibold">£{((item.quantity || 1) * (item.rate || 0)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Download Receipt Button */}
            {currentOrder.orderNumber && (
              <div className="mt-4">
                <button
                  onClick={() => {
                    generateReceiptPDF({
                      orderNumber: currentOrder.orderNumber,
                      createdAt: currentOrder.createdAt,
                      customer: currentOrder.customer,
                      items: currentOrder.items,
                      total: currentOrder.total,
                      paymentMethod: currentOrder.paymentMethod,
                      orderType: currentOrder.orderType
                    });
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <span className="text-xl">📄</span>
                  Download Receipt
                </button>
              </div>
            )}
          </div>
        )}

        {/* Help Link */}
        <div className="mt-6 text-center">
          <Link
            to="/voice-help"
            className="inline-flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-xl shadow-lg transition-all transform hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)'
            }}
          >
            <span className="text-2xl">❓</span>
            <span>Need Help with Voice Commands?</span>
          </Link>
        </div>

        {/* Connection Status */}
        <div className="mt-6 flex items-center justify-center space-x-2 text-sm text-gray-600">
          <div
            className="w-3 h-3 rounded-full"
            style={{
              backgroundColor:
                connectionStatus === 'connected' ? '#10b981' :
                connectionStatus === 'error' ? '#ef4444' :
                '#9ca3af'
            }}
          />
          <span>
            {connectionStatus === 'connected' ? 'Connected' :
             connectionStatus === 'error' ? 'Connection Error' :
             'Not Connected'}
          </span>
          <span className="opacity-50">•</span>
          <span className="opacity-70">Powered by QASTCO</span>
        </div>
      </div>
    </div>
  );
};

export default UltravoxVoiceInterface;
